import { Button, Flex, Form, Modal, Space } from 'antd';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useRef } from 'react';
import { history, RequestResponse, useDispatch, useIntl, useSelector } from 'umi';
import { processAPIResponseNow } from '@/utils/request';
import Drawer from '@/components/Drawer';
import * as LeadSelectors from '@/selectors/leadSelectors';
import {F, INTL_ACTION, INTL_ENTITY, getIntlTranslation} from '@/utils/intl';
import FEATURE_FLAGS from '@/constants/featureFlags';
import { useHasAccess, useHasFeatureFlag } from '@/utils/hooks';
import { ROUTE_PATHS } from '../../../../config/RoutePaths';
import styles from './index.less';
import ProjectDetails from '@/components/ProjectDetails';
import { RelatedLeadsDropdown } from '@/components/Lead/RelatedLeads';
import { LeftOutlined } from '@ant-design/icons';
import PERMISSIONS from '@/constants/permissions';
import { deleteLeadRequest } from '@/services/api/lead';

const LeadDetailsDrawer = () => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const detailsDrawer = useSelector(LeadSelectors.selectLeadDetailsDrawer);
  const leadId = detailsDrawer?.leadId;
  const canDelete = useHasAccess([PERMISSIONS.Leads.DELETE, PERMISSIONS.BUSINESS_PARTNER]);
  const lead = useSelector((state: DefaultRootState) =>
    LeadSelectors.selectLeadData(state, leadId),
  );
  const leadDetails =
    useSelector((state: DefaultRootState) =>
      LeadSelectors.selectLeadDetails(state, leadId),
    ) || lead;
  const hasLeadManagement = useHasFeatureFlag(FEATURE_FLAGS.LEAD_MANAGEMENT);
  const open = Boolean(leadId);

  const getDrawerWidth = () => (document.body.clientWidth > 1500 ? 1350 : 0.9 * document.body.clientWidth);

  const previousRoute = useRef<string | null>(null);

  useEffect(() => {
    if (!detailsDrawer?.ignoreRouting) {
      if (open) {
        previousRoute.current = window.location.pathname;
        window.history.pushState({}, '', `/lead/${leadId}`);
      } else if (
        previousRoute.current &&
        typeof previousRoute.current === 'string'
      ) {
        if (previousRoute.current.includes('/lead/'))
          return history.push(
            hasLeadManagement ? ROUTE_PATHS.LEADS_BOARD : ROUTE_PATHS.CALENDAR,
          );
        window.history.pushState({}, '', previousRoute.current);
      }
    }
  }, [open]);

  const dispatch = useDispatch();

  function toggle() {
    dispatch({
      type: 'leads/toggleLeadDetailsDrawer',
    });
  }

  function handleDelete() {
    Modal.confirm({
      title: getIntlTranslation(intl, { entity: INTL_ENTITY.LEAD, action: INTL_ACTION.REMOVE }),
      content: getIntlTranslation(intl, { entity: INTL_ENTITY.LEAD, action: INTL_ACTION.REMOVE_CONFIRMATION }),
      okText: intl.formatMessage({ id: 'common.actions.delete_permanently', defaultMessage: 'Delete Permanently' }),
      cancelText: intl.formatMessage({ id: 'common.actions.cancel', defaultMessage: 'Cancel' }),
      okButtonProps: { danger: true },
      onOk: async () => {
        const response = await deleteLeadRequest({ params: { leadId } }) as RequestResponse;
        processAPIResponseNow(response, {
          successMessage: getIntlTranslation(intl, { entity: INTL_ENTITY.LEAD, action: INTL_ACTION.REMOVE_SUCCESS }),
          showErrorMessage: true,
          onSuccess() {
            toggle();
            dispatch({ type: 'leads/fetch' });
          },
        });
      },
    });
  }

  useEffect(() => {
    if (isEmpty(detailsDrawer)) return;

    form.resetFields();
  }, [detailsDrawer]);

  const getTitle = () => (
    <Flex style={{width: "100%"}} justify={'space-between'}>
      <Space size={16}>
        <Button
          icon={<LeftOutlined />}
          onClick={toggle}
          className={styles.closeIcon}
        />
        <F id={'pages.lead.drawer.details'} />
      </Space>
      <Space>
        {leadDetails?.linkedLeads?.length > 0 && (
          <RelatedLeadsDropdown
            linkedLeads={leadDetails?.linkedLeads}
            currentLead={leadDetails}
          />
        )}
      </Space>
    </Flex>
  );

  return (
    <div className={styles.drawerContainer}>
      <Drawer
        data-testid="lead-details-drawer"
        width={getDrawerWidth()}
        title={getTitle()}
        open={open}
        destroyOnHidden
        className={styles.drawer}
        onOpenChange={toggle}
        hideCloseIcon
        rootClassName={styles.drawerRoot}
        drawerHeaderClassName={styles.drawerHeader}
        drawerBodyClassName={styles.drawerBody}
        toggledAt={detailsDrawer?.toggledAt}
      >
        <ProjectDetails
          projectId={leadId}
          onDelete={canDelete ? handleDelete : undefined}
          highlightedAppointmentId={detailsDrawer?.highlightedAppointmentId}
        />
      </Drawer>
    </div>
  );
};

export default React.memo(LeadDetailsDrawer, () => true);
